// Generic free-text/media validators for local send panels. No template or carousel-shape
// knowledge — every validate* function here takes plain strings and returns a plain error string. Moved
// out of carousel/CarouselSendPanel.js so a future panel type (GIF, location, LTO, voice call)
// reuses the same checks instead of re-implementing them. The carousel's messages and rules are unchanged.

export const DOUBLE_BRACE_RE = /\{\{|\}\}/;
export const DOUBLE_BRACE_ERROR = "Close the braces to save, like {{ customer.first_name }}.";
export const MEDIA_TOKEN_RE = /^\{[^{}]+\}$/;

/**
 * The library's isValidURL (Helper/Utils.tsx:238-245): the string parses as a URL.
 * @param {string} [value]
 * @returns {boolean}
 */
export function isValidUrl(value) {
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

// The library's containsVariablePlaceholder at 1.3.27 (Helper/Utils.tsx:247-250): a `{...}` anywhere in the value.
const containsVariablePlaceholder = (value) => /\{[^}]+\}/.test(value || '');

/**
 * Whether a value holds something typed: a string with a character other than white space in it.
 * @param {*} value
 * @returns {boolean}
 */
export const isFilled = (value) => typeof value === 'string' && value.trim() !== '';

/**
 * Counts `{{n}}` template-variable tokens in a string.
 * @param {string} [text]
 * @returns {number}
 */
export function countBraceTokens(text) {
  return (String(text || '').match(/\{\{\s*\d+\s*\}\}/g) || []).length;
}

/**
 * Required + double-brace check for a free-text field.
 * @param {string} [value]
 * @returns {string} empty string when valid, else an error message
 */
export function validateFreeText(value) {
  if (!value || !value.trim()) return 'This field is required.';
  if (DOUBLE_BRACE_RE.test(value)) return DOUBLE_BRACE_ERROR;
  return '';
}

/**
 * The check on a template's single dynamic button, as the library's Footer makes it (Components/Footer/index.tsx:60-65)
 * plus our double-brace rule: a DYNAMIC suffix must be filled in; a DYNAMIC_LINK_TRACK value must be filled in and be
 * either a `{token}` or a URL.
 * @param {string} [value]
 * @param {'DYNAMIC'|'DYNAMIC_LINK_TRACK'} buttonType
 * @returns {string} empty string when valid, else an error message
 */
export function validateDynamicButton(value, buttonType) {
  const err = validateFreeText(value);
  if (err) return err;
  if (buttonType === 'DYNAMIC_LINK_TRACK' && !containsVariablePlaceholder(value) && !isValidUrl(value)) {
    return 'Enter the full URL, or a customer attribute such as {customer.order_url}.';
  }
  return '';
}

// mediaType -> allowed extension(s). GIF templates are sent as an MP4 (Enterprise/WhatsApp has no
// animated-GIF wire format), same extension as VIDEO. DOCUMENT added for the Standard send panel
// (GIF/LOCATION/LTO/voice call) — validateMedia had no case for it before.
const MEDIA_EXTENSIONS = {
  VIDEO: 'mp4',
  GIF: 'mp4',
  DOCUMENT: 'pdf',
};
const DEFAULT_MEDIA_EXTENSIONS = 'jpg|jpeg|png';

/**
 * @param {string} [mediaType] 'VIDEO' | 'GIF' | 'DOCUMENT' | an image type
 * @returns {RegExp}
 */
function mediaUrlPattern(mediaType) {
  const ext = MEDIA_EXTENSIONS[mediaType] || DEFAULT_MEDIA_EXTENSIONS;
  return new RegExp(`^https?://.+\\.(${ext})(\\?.*)?$`, 'i');
}

/**
 * @param {string} [mediaType]
 * @returns {string} human-readable extension hint matching mediaUrlPattern above
 */
function mediaExtensionHint(mediaType) {
  if (mediaType === 'VIDEO' || mediaType === 'GIF') return '.mp4';
  if (mediaType === 'DOCUMENT') return '.pdf';
  return '.jpg, .jpeg or .png';
}

/**
 * Media token-or-URL check: a single `{token}`, or an https URL with the extension expected for
 * `mediaType`. With `urlOnly` the value must be the URL itself, as the library's Footer insists
 * (Components/Footer/index.tsx:52-56): no `{token}`, and the string has to parse as a URL.
 * @param {string} [value]
 * @param {string} [mediaType] 'VIDEO' | 'GIF' | 'DOCUMENT' | an image type
 * @param {{ urlOnly?: boolean }} [options]
 * @returns {string} empty string when valid, else an error message
 */
export function validateMedia(value, mediaType, { urlOnly = false } = {}) {
  if (!value || !value.trim()) return 'Media is required.';
  const trimmed = value.trim();
  if (!urlOnly && MEDIA_TOKEN_RE.test(trimmed)) return '';
  if (DOUBLE_BRACE_RE.test(trimmed)) return DOUBLE_BRACE_ERROR;
  if (mediaUrlPattern(mediaType).test(trimmed) && (!urlOnly || isValidUrl(trimmed))) return '';
  const hint = mediaExtensionHint(mediaType);
  return urlOnly
    ? `Enter an https link ending in ${hint}.`
    : `Enter a single token like {customer.image}, or an https link ending in ${hint}.`;
}
